/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    createRouter,
    createWebHistory,
    RouteLocationNormalized,
    NavigationGuard,
    NavigationGuardNext,
} from 'vue-router';

import store from '@/store';
import { i18n } from '@/utilities/i18n';
import { NavItem } from '@/interfaces/navigation/NavItem.types';
import routes from '@/routes';

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
});

const updateDocumentMeta = (toRoute: RouteLocationNormalized) => {
    const defaultTitle = 'Aquassist';
    const defaultDescription = 'Water Treatment and Management Platform';

    const navItems: NavItem[] = store.state.navigation.mainNavItems || [];
    const findNavItemByPath = (items: NavItem[], path: string): NavItem | undefined => {
        for (const item of items) {
            if (item.path === path) {
                return item;
            }
            if (item.subItems && item.subItems.length > 0) {
                const foundInSubItems = findNavItemByPath(item.subItems, path);
                if (foundInSubItems) {
                    return foundInSubItems;
                }
            }
        }
        return undefined;
    };

    const currentNavItem = findNavItemByPath(navItems, toRoute.path);

    let pageTitle = defaultTitle;
    let pageDescription = defaultDescription;

    if (currentNavItem && currentNavItem.title) {
        const translatedBaseTitle = i18n.global.t(currentNavItem.title);

        if (translatedBaseTitle) {
            pageTitle = translatedBaseTitle;
            pageDescription = currentNavItem.description ?? defaultDescription;
        } else {
            if (toRoute.name) {
                pageDescription = defaultDescription;
            }
        }
    }

    document.title = pageTitle;

    let metaDescriptionTag = document.querySelector('meta[name="description"]');
    if (!metaDescriptionTag) {
        metaDescriptionTag = document.createElement('meta');
        metaDescriptionTag.setAttribute('name', 'description');
        document.head.appendChild(metaDescriptionTag);
    }
    metaDescriptionTag.setAttribute('content', pageDescription);
};

/** Middlewares Implementation **/

interface MiddlewareContext {
    to: RouteLocationNormalized;
    from: RouteLocationNormalized;
    store: typeof store;
    next: NavigationGuardNext;
}

/**
 * Run the specified middlewares before going execute the next function of the router
 * @param {MiddlewareContext} context - the context of the route with to, from and next
 * @param {NavigationGuard[]} middlewares - list of the middlewares to execute
 * @param {number} index - the index of the middleware to execute in the array
 */
const runMiddlewares = async (
    context: MiddlewareContext,
    middlewares: NavigationGuard[],
    index: number,
): Promise<void | null> => {
    // Execute the middleware
    const hasNexted = await middlewares[index](context.to, context.from, context.next);

    // If the next function was executed, we stop the middleware.
    if (hasNexted) {
        return null;
    }

    // If there are more middlewares to execute, we are executing them
    if (middlewares.length - 1 > index) {
        return runMiddlewares(context, middlewares, index + 1);
    }

    // Run the "next" function of the router when all middlewares were executed.
    return context.next();
};

// Before each navigation (to a route) we are running the specified middlewares (if there is any)
router.beforeEach(async (to, from, next) => {
    updateDocumentMeta(to);

    // Will find the middlewares of current route and parents
    let middlewares: NavigationGuard[] = [];
    to.matched.forEach((route) => {
        if (route.meta && route.meta.middlewares && Array.isArray(route.meta.middlewares)) {
            middlewares = middlewares.concat(route.meta.middlewares as NavigationGuard[]);
        }
    });

    // If there are any middlewares, we are running them
    if (middlewares.length > 0) {
        const context = {
            to,
            from,
            store,
            next: (arg?: false | Error | string | object) => {
                // Wrapping the original next router function to return true if used.
                next(arg as any);
                return true;
            },
        } as MiddlewareContext;

        await runMiddlewares(context, middlewares, 0);
    } else {
        next();
    }
});

router.onError((error, to) => {
    if (error.name === 'ChunkLoadError' || error.message?.includes('Loading chunk')) {
        const targetPath = to?.fullPath || window.location.pathname;
        window.location.href = targetPath;
    }
});

export default router;
export { routes };
